import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { playCorrect, playClap } from '../../utils/sounds';
import { useAuth } from '../../contexts/AuthContext';
import LoginPromptModal from '../../components/LoginPromptModal';
import PremiumUpgradeModal from '../../components/PremiumUpgradeModal';

// ─── Theme ────────────────────────────────────────────────────────────────────
const PRIMARY = '#2DBCAF';
const PRIMARY_GR = 'linear-gradient(135deg, #2BBCAF 0%, #1fa99c 50%, #18938a 100%)';
const BG_GR = 'linear-gradient(160deg, #fff8f0 0%, #fff5eb 40%, #f0faf9 100%)';
const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Animations ───────────────────────────────────────────────────────────────
const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  40% { transform: scale(1.1) translateY(-8px); }
  70% { transform: scale(1.05) translateY(-4px); }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(-3deg); }
  50% { transform: translateY(-10px) rotate(3deg); }
`;
const pop = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const IntroOverlay = styled.div`
  position: fixed; inset: 0; z-index: 900;
  background: ${BG_GR};
  display: flex; flex-direction: column; overflow: hidden;
  font-family: ${FONT};
`;
const IntroWrapper = styled.div`
  width: 100%;
  min-height: 100dvh; display: flex; flex-direction: column;
  box-sizing: border-box;
`;
const TopNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.1);
  padding: 10px 16px; display: flex; align-items: center;
`;
const BackBtn = styled.button`
  width: 36px; height: 36px; background: transparent; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #333; .material-symbols-outlined { font-size: 26px; }
`;
const CharArea = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  position: relative; width: 100%; box-sizing: border-box;
`;
const FloatingIcon = styled.div<{ $top?: string; $left?: string; $right?: string; $bottom?: string; $delay?: number }>`
  position: absolute;
  top: ${p => p.$top ?? 'auto'}; left: ${p => p.$left ?? 'auto'};
  right: ${p => p.$right ?? 'auto'}; bottom: ${p => p.$bottom ?? 'auto'};
  font-size: 40px;
  animation: ${float} 3s ease-in-out ${p => p.$delay ?? 0}s infinite;
`;
const CharCard = styled.div`
  font-size: clamp(80px, 22vw, 120px);
  animation: ${bounce} 2s ease-in-out infinite;
  filter: drop-shadow(0 12px 28px rgba(0,0,0,0.1));
`;

const IntroImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  width: 100%;
  max-width: 220px;
  margin: 0 auto 0 calc(50% - 130px);
  animation: ${fadeInScale} 0.6s ease;
`;

const IntroImageCard = styled.div<{ $delay?: number }>`
  width: 100%;
  aspect-ratio: 1;
  background: #fff;
  border-radius: 16px;
  padding: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  animation: ${bounce} 3s ease-in-out ${p => p.$delay ?? 0}s infinite;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const FloatingPaint = styled.div<{ $color: string; $top?: string; $left?: string; $right?: string; $delay?: number }>`
  position: absolute;
  top: ${p => p.$top ?? 'auto'}; left: ${p => p.$left ?? 'auto'}; right: ${p => p.$right ?? 'auto'};
  width: 20px; height: 20px;
  background: ${p => p.$color};
  border-radius: 50%;
  animation: ${float} 2.5s ease-in-out ${p => p.$delay ?? 0}s infinite;
  box-shadow: 0 4px 12px ${p => p.$color}66;
`;
const InfoCard = styled.div`
  background: #fff; border-radius: 32px 32px 0 0;
  padding: 24px 24px max(32px, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 32px rgba(0,0,0,0.08);
  animation: ${fadeInUp} 0.6s ease;
`;
const Handle = styled.div`
  width: 40px; height: 4px; background: #e0e0e0;
  border-radius: 99px; margin: 0 auto 18px;
`;
const IntroTitle = styled.h1`
  font-size: clamp(26px, 7vw, 32px); font-weight: 900;
  margin: 0 0 8px; color: #1a2a2a;
  span { color: ${PRIMARY}; }
`;
const IntroDesc = styled.p`
  font-size: 14px; color: #7a8a8a; line-height: 1.6; margin: 0 0 20px;
`;
const StartBtn = styled.button`
  width: 100%; padding: 16px 24px; border-radius: 99px; border: none;
  background: ${PRIMARY_GR}; color: #fff;
  font-size: 17px; font-weight: 800;
  cursor: pointer; box-shadow: 0 6px 24px rgba(43,188,175,0.4);
  font-family: ${FONT};
`;

// ─── Game Styled Components ───────────────────────────────────────────────────
const GameWrap = styled.div`
  min-height: 100dvh; display: flex; flex-direction: column;
  background: #f8f9fa; font-family: ${FONT};
`;
const GameNav = styled.header`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 16px;
  display: flex; align-items: center; justify-content: space-between;
`;
const NavLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
const GameTitle = styled.span`font-size: 17px; font-weight: 800; color: #1a2a2a;`;
const NavRight = styled.div`display: flex; gap: 8px;`;
const MuteBtn = styled.button`
  width: 36px; height: 36px; border-radius: 10px;
  background: #f1f5f9; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: #64748b; transition: all 0.2s;
  .material-symbols-outlined { font-size: 20px; }
  &:hover { background: #e2e8f0; }
`;
const NavBtn = styled.button<{ $active?: boolean }>`
  padding: 8px 14px; border-radius: 10px; border: none;
  background: ${p => p.$active ? PRIMARY : '#f1f5f9'};
  color: ${p => p.$active ? 'white' : '#64748b'};
  font-size: 12px; font-weight: 700; cursor: pointer;
  font-family: ${FONT}; display: flex; align-items: center; gap: 4px;
`;

const CanvasArea = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  background: white;
  overflow: hidden;
`;

const StyledCanvas = styled.canvas`
  max-width: 100%;
  max-height: 55vh;
  touch-action: none;
  cursor: crosshair;
`;

const ColorPalette = styled.div`
  background: white;
  padding: 16px; padding-bottom: max(20px, env(safe-area-inset-bottom));
  border-top: 1px solid #e2e8f0;
`;

const PaletteLabel = styled.div`
  font-size: 12px; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.5px;
  margin-bottom: 12px; text-align: center;
`;

const ColorGrid = styled.div`
  display: flex; flex-wrap: wrap; gap: 10px;
  justify-content: center; max-width: 400px; margin: 0 auto;
`;

const ColorBtn = styled.button<{ $color: string; $selected?: boolean }>`
  width: 40px; height: 40px; border-radius: 12px;
  background: ${p => p.$color};
  border: 3px solid ${p => p.$selected ? '#1a2a2a' : 'transparent'};
  cursor: pointer; transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  animation: ${p => p.$selected ? pop : 'none'} 0.3s ease;
  &:hover { transform: scale(1.1); }
`;

const EraserBtn = styled.button<{ $selected?: boolean }>`
  width: 40px; height: 40px; border-radius: 12px;
  background: linear-gradient(135deg, #fff 45%, #f0f0f0 55%);
  border: 3px solid ${p => p.$selected ? '#1a2a2a' : '#e2e8f0'};
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  .material-symbols-outlined { font-size: 20px; color: #64748b; }
  &:hover { transform: scale(1.1); }
`;

const TemplateSelectorWrap = styled.div`
  display: flex; align-items: center; gap: 4px;
  background: white; border-bottom: 1px solid #e2e8f0;
  padding: 8px 4px;
`;

const TemplateSelector = styled.div`
  display: flex; gap: 10px; overflow-x: auto; padding: 8px 8px;
  flex: 1;
  &::-webkit-scrollbar { display: none; }
`;

const ScrollArrow = styled.button`
  flex-shrink: 0; width: 32px; height: 32px;
  border-radius: 50%; border: none;
  background: #f1f5f9; color: #64748b;
  cursor: pointer; display: flex;
  align-items: center; justify-content: center;
  transition: all 0.2s;
  &:hover { background: #e2e8f0; color: #334155; }
  &:active { transform: scale(0.95); }
`;

const TemplateBtn = styled.button<{ $active?: boolean; $locked?: boolean }>`
  flex-shrink: 0; padding: 10px 16px; border-radius: 12px;
  background: ${p => p.$locked ? '#fef3c7' : p.$active ? `${PRIMARY}15` : '#f8fafc'};
  border: 2px solid ${p => p.$locked ? '#fcd34d' : p.$active ? PRIMARY : '#e2e8f0'};
  color: ${p => p.$locked ? '#b45309' : p.$active ? PRIMARY : '#64748b'};
  font-size: 13px; font-weight: 700; cursor: pointer;
  font-family: ${FONT}; white-space: nowrap;
  display: flex; align-items: center; gap: 6px;
  opacity: ${p => p.$locked ? 0.85 : 1};
`;

const LoadingOverlay = styled.div`
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.9);
  font-size: 14px; color: #64748b;
`;

const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
`;
const Modal = styled.div`
  background: #fff; border-radius: 28px; padding: 28px 24px;
  text-align: center; max-width: 320px; width: 88%;
  animation: ${fadeInScale} 0.4s ease;
`;
const ModalEmoji = styled.div`font-size: 56px; margin-bottom: 12px;`;
const ModalTitle = styled.div`font-size: 24px; font-weight: 900; color: #1a2a2a; margin-bottom: 8px;`;
const ModalDesc = styled.div`font-size: 14px; color: #7a8a8a; margin-bottom: 20px;`;
const ModalBtnRow = styled.div`display: flex; gap: 10px; justify-content: center;`;
const ModalBtn = styled.button<{ $primary?: boolean }>`
  padding: 12px 24px; border-radius: 99px;
  border: ${p => p.$primary ? 'none' : '2px solid #e0e0e0'};
  background: ${p => p.$primary ? PRIMARY_GR : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#555'};
  font-size: 14px; font-weight: 700; cursor: pointer;
  font-family: ${FONT};
`;

// ─── Color Palette ────────────────────────────────────────────────────────────
const COLORS = [
  '#ef4444', '#f97316', '#facc15', '#22c55e', '#14b8a6',
  '#3b82f6', '#8b5cf6', '#ec4899', '#f472b6', '#78716c',
  '#1e293b', '#ffffff',
];

// ─── Templates ────────────────────────────────────────────────────────────────
interface Template {
  id: string;
  name: string;
  emoji: string;
  src: string;
  isPremium?: boolean;
}

const FALLBACK_TEMPLATES: Template[] = [
  { id: 'template5', name: 'Rubah', emoji: '', src: '/assets/coloring/template5.svg' },
  { id: 'template6', name: 'Gajah', emoji: '', src: '/assets/coloring/template6.svg' },
  { id: 'template7', name: 'Anjing', emoji: '', src: '/assets/coloring/template7.svg' },
  { id: 'template1', name: 'Kelinci', emoji: '', src: '/assets/coloring/template1.svg' },
  { id: 'template2', name: 'Burung', emoji: '', src: '/assets/coloring/template2.svg' },
  { id: 'template3', name: 'Ular', emoji: '', src: '/assets/coloring/template3.svg' },
  { id: 'template4', name: 'Kura-kura', emoji: '', src: '/assets/coloring/template4.svg' },
  { id: 'dino', name: 'Dino', emoji: '', src: '/assets/coloring/nature.svg' },
  { id: 'antariksa', name: 'Antariksa', emoji: '', src: '/assets/coloring/animals.svg' },
  { id: 'astronot', name: 'Astronot', emoji: '', src: '/assets/coloring/winter.svg' },
];

// ─── Flood Fill Algorithm ─────────────────────────────────────────────────────
function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result 
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
}

function colorsMatch(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number, tolerance = 32): boolean {
  return Math.abs(r1 - r2) <= tolerance && Math.abs(g1 - g2) <= tolerance && Math.abs(b1 - b2) <= tolerance;
}

// Check if pixel is part of outline (dark enough to be considered a line)
function isOutlinePixel(r: number, g: number, b: number): boolean {
  // Consider dark pixels as outline - threshold 100 catches dark grays too
  const brightness = (r + g + b) / 3;
  return brightness < 100;
}

function floodFill(
  ctx: CanvasRenderingContext2D,
  startX: number,
  startY: number,
  fillColor: [number, number, number]
): void {
  const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
  const data = imageData.data;
  const width = ctx.canvas.width;
  const height = ctx.canvas.height;
  
  const startIdx = (startY * width + startX) * 4;
  const startR = data[startIdx];
  const startG = data[startIdx + 1];
  const startB = data[startIdx + 2];
  
  // Don't fill if clicking on outline (dark colors) or same color
  if (isOutlinePixel(startR, startG, startB)) return;
  if (colorsMatch(startR, startG, startB, fillColor[0], fillColor[1], fillColor[2], 5)) return;
  
  const stack: [number, number][] = [[startX, startY]];
  const visited = new Set<number>();
  
  while (stack.length > 0) {
    const [x, y] = stack.pop()!;
    const idx = (y * width + x) * 4;
    
    if (x < 0 || x >= width || y < 0 || y >= height) continue;
    if (visited.has(idx)) continue;
    
    const r = data[idx];
    const g = data[idx + 1];
    const b = data[idx + 2];
    
    // Stop at dark outlines
    if (isOutlinePixel(r, g, b)) continue;
    
    // Only fill if color matches start color
    if (!colorsMatch(r, g, b, startR, startG, startB, 32)) continue;
    
    visited.add(idx);
    data[idx] = fillColor[0];
    data[idx + 1] = fillColor[1];
    data[idx + 2] = fillColor[2];
    data[idx + 3] = 255;
    
    stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
  }
  
  ctx.putImageData(imageData, 0, 0);
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ColoringGame() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const templateScrollRef = useRef<HTMLDivElement>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const musicGainRef = useRef<GainNode | null>(null);
  const musicIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showIntro, setShowIntro] = useState(true);
  const [currentTemplate, setCurrentTemplate] = useState(0);
  const [selectedColor, setSelectedColor] = useState(COLORS[0]);
  const [loading, setLoading] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [colorCount, setColorCount] = useState(0);
  const [isEraser, setIsEraser] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [templates, setTemplates] = useState<Template[]>(FALLBACK_TEMPLATES);
  const [savedProgressIds, setSavedProgressIds] = useState<Set<string>>(new Set());
  const [loadingProgress, setLoadingProgress] = useState(false);
  const lastSaveRef = useRef<number>(0);

  // Fetch saved progress list for current user
  const fetchSavedProgress = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch('/api/coloring-progress', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSavedProgressIds(new Set(data.map((p: any) => p.templateId)));
      }
    } catch (err) {
      console.warn('Failed to fetch saved progress');
    }
  }, [user]);

  // Save canvas progress
  const saveProgress = useCallback(async (templateId: string) => {
    if (!user || !canvasRef.current) return;
    const now = Date.now();
    if (now - lastSaveRef.current < 3000) return; // Throttle saves to every 3 seconds
    lastSaveRef.current = now;
    
    try {
      const canvasData = canvasRef.current.toDataURL('image/jpeg', 0.6);
      await fetch(`/api/coloring-progress/${templateId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ canvasData }),
      });
      setSavedProgressIds(prev => new Set(prev).add(templateId));
    } catch (err) {
      console.warn('Failed to save progress');
    }
  }, [user]);

  // Load saved progress for a template
  const loadSavedProgress = useCallback(async (templateId: string) => {
    if (!user) return null;
    try {
      const res = await fetch(`/api/coloring-progress/${templateId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.hasProgress) return data.canvasData;
      }
    } catch (err) {
      console.warn('Failed to load progress');
    }
    return null;
  }, [user]);

  // Delete saved progress (on reset)
  const deleteProgress = useCallback(async (templateId: string) => {
    if (!user) return;
    try {
      await fetch(`/api/coloring-progress/${templateId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setSavedProgressIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(templateId);
        return newSet;
      });
    } catch (err) {
      console.warn('Failed to delete progress');
    }
  }, [user]);

  // Fetch saved progress on mount
  useEffect(() => {
    fetchSavedProgress();
  }, [fetchSavedProgress]);

  // Fetch templates from API
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await fetch('/api/coloring-templates');
        if (res.ok) {
          const data = await res.json();
          if (data.length > 0) {
            setTemplates(data.map((t: any) => ({
              id: t.templateId,
              name: t.name,
              emoji: '',
              src: t.src,
              isPremium: t.isPremium,
            })));
          }
        }
      } catch (err) {
        console.warn('Using fallback templates');
      }
    };
    fetchTemplates();
  }, []);

  const template = templates[currentTemplate];
  const isPremiumUser = user?.subscription?.status === 'active' || user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';

  // ── Background Music ──────────────────────────────────────────────────────
  const startMusic = useCallback(() => {
    if (audioCtxRef.current) return;
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioCtxRef.current = ctx;

      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(0.15, ctx.currentTime);
      masterGain.connect(ctx.destination);
      musicGainRef.current = masterGain;

      // Cheerful pentatonic melody for coloring
      const notes = [523.25, 587.33, 659.25, 783.99, 880.00, 783.99, 659.25, 587.33]; // C5 D5 E5 G5 A5
      const noteDur = 0.5;
      const loopLen = notes.length * noteDur;

      const scheduleLoop = (startAt: number) => {
        notes.forEach((freq, i) => {
          const t = startAt + i * noteDur;
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = freq;
          const g = ctx.createGain();
          g.gain.setValueAtTime(0, t);
          g.gain.linearRampToValueAtTime(0.4, t + 0.03);
          g.gain.exponentialRampToValueAtTime(0.001, t + noteDur * 0.9);
          osc.connect(g);
          g.connect(masterGain);
          osc.start(t);
          osc.stop(t + noteDur);
        });

        // Soft pad chord
        [261.63, 329.63, 392.00].forEach(freq => {
          const pad = ctx.createOscillator();
          pad.type = 'triangle';
          pad.frequency.value = freq * 0.5;
          const pg = ctx.createGain();
          pg.gain.setValueAtTime(0.05, startAt);
          pg.gain.linearRampToValueAtTime(0.08, startAt + loopLen * 0.3);
          pg.gain.linearRampToValueAtTime(0.05, startAt + loopLen);
          pad.connect(pg);
          pg.connect(masterGain);
          pad.start(startAt);
          pad.stop(startAt + loopLen);
        });
      };

      scheduleLoop(ctx.currentTime + 0.1);
      scheduleLoop(ctx.currentTime + 0.1 + loopLen);

      musicIntervalRef.current = setInterval(() => {
        if (!audioCtxRef.current) return;
        scheduleLoop(audioCtxRef.current.currentTime + loopLen);
      }, loopLen * 1000);
    } catch (_) { /* audio not supported */ }
  }, []);

  const stopMusic = useCallback(() => {
    if (musicIntervalRef.current) {
      clearInterval(musicIntervalRef.current);
      musicIntervalRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
      musicGainRef.current = null;
    }
  }, []);

  // Start/stop music based on intro state
  useEffect(() => {
    if (!showIntro && !isMuted) startMusic();
    return () => stopMusic();
  }, [showIntro, startMusic, stopMusic]);

  // Handle mute toggle
  useEffect(() => {
    if (musicGainRef.current && audioCtxRef.current) {
      musicGainRef.current.gain.setTargetAtTime(isMuted ? 0 : 0.15, audioCtxRef.current.currentTime, 0.1);
    }
  }, [isMuted]);

  // Load image onto canvas
  const loadTemplate = useCallback(async (src: string, templateId: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    setLoading(true);
    setLoadingProgress(true);
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    // Check for saved progress first
    const savedData = await loadSavedProgress(templateId);
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = async () => {
      // Use higher resolution for sharper lines
      const maxWidth = Math.min(600, window.innerWidth - 32);
      const scale = maxWidth / img.width;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      
      canvas.width = img.width * scale * pixelRatio;
      canvas.height = img.height * scale * pixelRatio;
      canvas.style.width = `${img.width * scale}px`;
      canvas.style.height = `${img.height * scale}px`;
      
      // Disable smoothing for sharp lines
      ctx.imageSmoothingEnabled = false;
      
      // Fill white background then draw image
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // If we have saved progress, overlay it
      if (savedData) {
        const savedImg = new Image();
        savedImg.onload = () => {
          ctx.drawImage(savedImg, 0, 0, canvas.width, canvas.height);
          setLoading(false);
          setLoadingProgress(false);
        };
        savedImg.onerror = () => {
          setLoading(false);
          setLoadingProgress(false);
        };
        savedImg.src = savedData;
      } else {
        setLoading(false);
        setLoadingProgress(false);
      }
    };
    img.onerror = () => {
      // If SVG fails, show placeholder
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(0, 0, 400, 400);
      ctx.fillStyle = '#94a3b8';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Gambar tidak tersedia', 200, 200);
      setLoading(false);
      setLoadingProgress(false);
    };
    img.src = src;
  }, [loadSavedProgress]);

  // Load template when changed
  useEffect(() => {
    if (!showIntro && template) {
      loadTemplate(template.src, template.id);
    }
  }, [showIntro, template?.src, template?.id, loadTemplate]);

  // Handle canvas click
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Prevent double-firing on touch devices
    if ('touches' in e) {
      e.preventDefault();
    }
    
    const rect = canvas.getBoundingClientRect();
    let clientX: number, clientY: number;
    
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = Math.floor((clientX - rect.left) * scaleX);
    const y = Math.floor((clientY - rect.top) * scaleY);
    
    // Check if clicked on outline (dark pixels) - don't allow coloring
    const imageData = ctx.getImageData(x, y, 1, 1).data;
    if (isOutlinePixel(imageData[0], imageData[1], imageData[2])) {
      return; // Don't color the outline
    }
    
    // Use white for eraser, selected color otherwise
    const fillRgb = isEraser ? hexToRgb('#ffffff') : hexToRgb(selectedColor);
    floodFill(ctx, x, y, fillRgb);
    
    // No sound during coloring - sound only plays when "Selesai" button is clicked
    
    const newCount = colorCount + 1;
    setColorCount(newCount);
    
    // Auto-save progress for logged in users
    if (user && template) {
      saveProgress(template.id);
    }
    
    // Show login prompt after 5 colors if not logged in
    if (!user && !authLoading && newCount === 5) {
      setTimeout(() => setShowLoginPrompt(true), 500);
    }
  }, [selectedColor, colorCount, user, authLoading, isEraser, template, saveProgress]);

  const handleReset = () => {
    deleteProgress(template.id);
    loadTemplate(template.src, template.id);
  };

  const handleDone = () => {
    playClap();
    setShowComplete(true);
  };

  const handleNextTemplate = () => {
    setShowComplete(false);
    
    // Find next non-premium template (or show premium modal if all remaining are premium)
    let nextIdx = (currentTemplate + 1) % templates.length;
    let checkedCount = 0;
    
    while (checkedCount < templates.length) {
      const nextTemplate = templates[nextIdx];
      const isLocked = nextTemplate.isPremium && !isPremiumUser;
      
      if (!isLocked) {
        setCurrentTemplate(nextIdx);
        return;
      }
      
      nextIdx = (nextIdx + 1) % templates.length;
      checkedCount++;
    }
    
    // All templates are premium - show premium modal
    if (!user) {
      setShowLoginPrompt(true);
    } else {
      setShowPremiumModal(true);
    }
  };

  const scrollTemplates = (direction: 'left' | 'right') => {
    if (templateScrollRef.current) {
      const scrollAmount = 150;
      templateScrollRef.current.scrollBy({
        left: direction === 'right' ? scrollAmount : -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (showIntro) {
    return (
      <IntroOverlay>
        <IntroWrapper>
          <TopNav>
            <BackBtn onClick={() => navigate('/games')}>
              <span className="material-symbols-outlined">arrow_back</span>
            </BackBtn>
          </TopNav>
          <CharArea>
            <IntroImageGrid>
              {templates.slice(0, 4).map((t, i) => (
                <IntroImageCard key={t.id} $delay={i * 0.15}>
                  <img src={t.src} alt={t.name} />
                </IntroImageCard>
              ))}
            </IntroImageGrid>
          </CharArea>
          <InfoCard>
            <Handle />
            <IntroTitle>Mewarnai</IntroTitle>
            <IntroDesc>
              Pilih warna favoritmu, lalu sentuh bagian gambar untuk mewarnainya. 
              Buat karya seni yang indah!
            </IntroDesc>
            <StartBtn onClick={() => setShowIntro(false)}>Mulai Mewarnai</StartBtn>
          </InfoCard>
        </IntroWrapper>
      </IntroOverlay>
    );
  }

  return (
    <GameWrap>
      <GameNav>
        <NavLeft>
          <BackBtn onClick={() => setShowIntro(true)}>
            <span className="material-symbols-outlined">arrow_back</span>
          </BackBtn>
          <GameTitle>Mewarnai</GameTitle>
        </NavLeft>
        <NavRight>
          <MuteBtn onClick={() => setIsMuted(m => !m)} title={isMuted ? 'Nyalakan musik' : 'Matikan musik'}>
            <span className="material-symbols-outlined">{isMuted ? 'volume_off' : 'volume_up'}</span>
          </MuteBtn>
          <NavBtn onClick={handleReset}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>restart_alt</span>
          </NavBtn>
          <NavBtn $active onClick={handleDone}>Selesai ✓</NavBtn>
        </NavRight>
      </GameNav>

      <TemplateSelectorWrap>
        <ScrollArrow onClick={() => scrollTemplates('left')}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_left</span>
        </ScrollArrow>
        <TemplateSelector ref={templateScrollRef}>
          {templates.map((t, idx) => {
            const isLocked = t.isPremium && !isPremiumUser;
            const hasSavedProgress = savedProgressIds.has(t.id);
            return (
              <TemplateBtn
                key={t.id}
                $active={currentTemplate === idx}
                $locked={isLocked}
                onClick={() => {
                  if (isLocked) {
                    if (!user) {
                      setShowLoginPrompt(true);
                    } else {
                      setShowPremiumModal(true);
                    }
                  } else {
                    setCurrentTemplate(idx);
                  }
                }}
              >
                {isLocked && <span className="material-symbols-outlined" style={{ fontSize: 14 }}>lock</span>}
                {!isLocked && hasSavedProgress && <span style={{ fontSize: 10, color: '#22c55e' }}>●</span>}
                {t.emoji && <span>{t.emoji}</span>}
                {t.name}
                {t.isPremium && <span style={{ fontSize: 10, marginLeft: 2 }}>⭐</span>}
              </TemplateBtn>
            );
          })}
        </TemplateSelector>
        <ScrollArrow onClick={() => scrollTemplates('right')}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_right</span>
        </ScrollArrow>
      </TemplateSelectorWrap>

      <CanvasArea>
        <StyledCanvas
          ref={canvasRef}
          onClick={handleCanvasClick}
          onTouchStart={handleCanvasClick}
        />
        {loading && <LoadingOverlay>Memuat gambar...</LoadingOverlay>}
      </CanvasArea>

      <ColorPalette>
        <PaletteLabel>
          {isEraser ? '🧹 Mode Hapus' : '🎨 Pilih Warna'}
        </PaletteLabel>
        <ColorGrid>
          {COLORS.map(color => (
            <ColorBtn
              key={color}
              $color={color}
              $selected={!isEraser && selectedColor === color}
              onClick={() => { setSelectedColor(color); setIsEraser(false); }}
            />
          ))}
          <EraserBtn $selected={isEraser} onClick={() => setIsEraser(true)}>
            <span className="material-symbols-outlined">ink_eraser</span>
          </EraserBtn>
        </ColorGrid>
      </ColorPalette>

      {showComplete && (
        <ModalOverlay onClick={() => setShowComplete(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalEmoji>🎉</ModalEmoji>
            <ModalTitle>Bagus Sekali!</ModalTitle>
            <ModalDesc>Karyamu sangat indah! Mau warnai gambar lain?</ModalDesc>
            <ModalBtnRow>
              <ModalBtn onClick={() => navigate('/games')}>Menu</ModalBtn>
              <ModalBtn $primary onClick={handleNextTemplate}>Gambar Lain</ModalBtn>
            </ModalBtnRow>
          </Modal>
        </ModalOverlay>
      )}

      {showLoginPrompt && !user && !authLoading && (
        <LoginPromptModal
          onClose={() => setShowLoginPrompt(false)}
          subtitle="Login untuk menyimpan karya mewarnaimu!"
        />
      )}

      {showPremiumModal && (
        <PremiumUpgradeModal
          isOpen={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          featureName="gambar mewarnai eksklusif"
        />
      )}
    </GameWrap>
  );
}
